import { fmtDateLong } from './date-format.util';

/**
 * The "Rate your experience" form, shared by the two places a customer can reach it: the booking
 * detail's "How was your event?" card and the "Leave a review" chip on the bookings LIST — which
 * opens it as a popup over the list (mockup `cbkReviewHint` → `data-revopen` → `openReviewForm`,
 * which fills `detailBody` and opens `detailModal`; it never leaves the list).
 *
 * Both entry points must show the same thing, so the model is built here from booking rows rather
 * than derived twice. Ratings are per SERVICE plus one overall row, and each service's rating is
 * attributed to the vendor who DELIVERS it — a partner-owned package rates the partner, not the
 * reseller who sold it.
 */

/** One rateable line: a booked service, or the overall experience (`offeringId === null`). */
export interface ReviewLine {
  offeringId: number | null;
  name: string;                       // the service being rated ('' on the overall line)
  ownerName: string;                  // the vendor who actually delivers it
  ownerVendorId: number;              // Review.VendorId this line is written against
  rating: number;                     // 0 = not rated yet
  text: string;
  replyText: string;                  // the vendor's public reply ('' = none yet)
}

/**
 * `visible` is DERIVED from the booking being completed, and `reviewed` from review rows existing —
 * never from whether a button was pressed. Before: the prompt + "Leave a review". After: the stars
 * they gave, with an Edit affordance that reopens the same form.
 */
export interface ReviewFormModel {
  visible: boolean;
  reviewed: boolean;
  vendorName: string;
  eventDateLong: string;
  authorName: string;                 // Review.Author — NOT NULL, so never blank
  overall: ReviewLine;
  services: ReviewLine[];
}

/** The booking columns the form needs. */
export interface ReviewBookingLike {
  status?: string | null;
  resellerVendorId?: number | null;
  eventDate?: string | null;
  contactName?: string | null;
}

/** The booked-service columns the form needs. Pass ACTIVE packages only. */
export interface ReviewPackageLike {
  offeringId?: number | null;
  vendorId?: number | null;
  name?: string | null;
}

/** The Review columns the form needs to seed an edit. */
export interface ReviewRowLike {
  offeringId?: number | null;
  rating?: number | null;
  reviewText?: string | null;
  replyText?: string | null;
  isDeleted?: boolean | null;
}

export function emptyReviewFormModel(): ReviewFormModel {
  return {
    visible: false, reviewed: false, vendorName: '', eventDateLong: '', authorName: '',
    overall: { offeringId: null, name: '', ownerName: '', ownerVendorId: 0, rating: 0, text: '', replyText: '' },
    services: [],
  };
}

/**
 * Build the form for one booking. Seeded from whatever the customer already said, so re-opening
 * EDITS the review they left rather than stacking a second one on the same booking.
 */
export function buildReviewFormModel(args: {
  booking: ReviewBookingLike | null;
  /** Active packages of this booking (removed / cancelled lines already filtered out). */
  packages: ReviewPackageLike[];
  /** This booking's Review rows. */
  reviews: ReviewRowLike[];
  vendorName: (id?: number | null) => string;
  /** The customer's profile name; the booking's contact name stands in when it's blank. */
  customerName?: string | null;
}): ReviewFormModel {
  const b = args.booking;
  if (!b) return emptyReviewFormModel();
  const rows = args.reviews.filter(r => !r.isDeleted);
  const line = (offeringId: number | null, name: string, ownerName: string, ownerVendorId: number): ReviewLine => {
    const r = rows.find(x => (x.offeringId ?? null) === offeringId);
    return {
      offeringId, name, ownerName, ownerVendorId,
      rating: r?.rating ?? 0,
      text: (r?.reviewText ?? '').trim(),
      replyText: (r?.replyText ?? '').trim(),
    };
  };
  const seen = new Set<number>();
  const services: ReviewLine[] = [];
  for (const p of args.packages) {
    const offId = p.offeringId ?? 0;
    if (!offId || seen.has(offId)) continue;
    seen.add(offId);
    // The rating belongs to the vendor who DELIVERS the service, not the reseller who sold it —
    // a partner-owned package rates the partner. Fall back to the reseller when unowned.
    const ownerId = p.vendorId ?? b.resellerVendorId ?? 0;
    services.push(line(offId, p.name ?? 'Service', args.vendorName(ownerId), ownerId));
  }
  return {
    visible: b.status === 'completed',
    reviewed: rows.length > 0,
    vendorName: args.vendorName(b.resellerVendorId),
    eventDateLong: fmtDateLong(b.eventDate ?? ''),
    authorName: (args.customerName || b.contactName || '').trim() || 'A Trexa customer',
    overall: line(null, '', '', b.resellerVendorId ?? 0),
    services,
  };
}
